import { format } from "date-fns";
import React from "react";
import { t } from "ttag";

import { constants, templates } from "@reactivated";

import { HLevel } from "../components/Headings";
import { ArticleHero, AspectRatio } from "../components/Heros/Article";
import {
    MetaData,
    MetaDataItem,
    MetaDataPillsItem,
} from "../components/MetaData";
import { PageSkeleton } from "../components/PageSkeleton";
import { toolboxComponents } from "../components/PageSkeleton/Toolbox";
import { TrackedStreamField } from "../components/PageSkeleton/TrackedStreamField";
import { PreferredSourceButton } from "../components/PreferredSourceButton";

import styles from "./article.module.scss";

const validateAspectRatio = (r: string): undefined | AspectRatio => {
    if (
        Object.values(
            constants["server.cms.types.ArticleAspectRatioChoices"],
        ).includes(r as AspectRatio)
    ) {
        return r as AspectRatio;
    } else {
        return undefined;
    }
};

export const Template = (props: templates.ArticlePageTemplate) => {
    return (
        <PageSkeleton
            {...props.page}
            isPreview={props.is_preview}
            // The hero block renders the page title as a visible <h1>.
            hasVisibleH1={props.page.body.value.some(
                (block) => block.type === "hero",
            )}
        >
            <article className={styles.root}>
                <HLevel>
                    <TrackedStreamField
                        tagName={React.Fragment}
                        value={props.page.body.value}
                        components={{
                            ...toolboxComponents,
                            hero: (block) => (
                                <HLevel level={1}>
                                    <ArticleHero
                                        category={props.page.article_type!}
                                        title={props.page.title}
                                        subtitle={props.page.subtitle}
                                        url={props.page.canonical_url}
                                        media={props.page.image?.block}
                                        mediaAspectRatio={validateAspectRatio(
                                            props.page.image_aspect_ratio,
                                        )}
                                        {...block.value}
                                    />
                                </HLevel>
                            ),
                            metadata: () => {
                                const audience = props.page.tags.filter(
                                    (tag) => tag.category === "audience",
                                );
                                const topic = props.page.tags.filter(
                                    (tag) => tag.category === "topic",
                                );
                                return (
                                    <MetaData>
                                        {props.page.author && (
                                            <MetaDataItem label={t`Written by`}>
                                                <address>
                                                    {props.page.author}
                                                </address>
                                            </MetaDataItem>
                                        )}
                                        <MetaDataItem label={t`Last updated`}>
                                            <time
                                                dateTime={
                                                    props.page.last_modified
                                                }
                                            >
                                                {format(
                                                    new Date(
                                                        props.page.last_modified,
                                                    ),
                                                    "MMMM d, yyyy",
                                                )}
                                            </time>
                                        </MetaDataItem>
                                        {audience.length ? (
                                            <MetaDataPillsItem
                                                label={t`This is for`}
                                                links={audience}
                                            />
                                        ) : null}
                                        {topic.length ? (
                                            <MetaDataPillsItem
                                                label={t`Topics`}
                                                links={topic}
                                            />
                                        ) : null}
                                    </MetaData>
                                );
                            },
                        }}
                    />
                </HLevel>
                <PreferredSourceButton />
            </article>
        </PageSkeleton>
    );
};
